/* The daily goal and the streak: hands answered per day, in the user's own day, against a
   number of hands they set themselves. The counting per day is the database's
   (`practice_days`); what a streak is, is here, pure. docs/specs/2026-09-20-daily-goal-design.md */

import { z } from "@preflop/core";
import type { SupabaseClient } from "@supabase/supabase-js";

/** Hands a day. 0 is no goal at all, and takes the feature off the screen. */
export const GOAL_DEFAULT = 50;
export const GOAL_MIN = 1;
export const GOAL_MAX = 1000;
/** How far back the days are read, which is also the longest streak that can be told. */
export const LOOK_BACK_DAYS = 400;

/** The goal as a profile's settings hold it: whatever an older build, a newer one or a hand
    left there. Nothing, or nonsense, is the default; 0 is 0; the rest is brought in range. */
export function readGoal(raw: unknown): number {
  if (typeof raw !== "number" || !Number.isFinite(raw)) return GOAL_DEFAULT;
  if (raw <= 0) return 0;
  return Math.min(GOAL_MAX, Math.max(GOAL_MIN, Math.round(raw)));
}

/** The calendar day a moment falls on in a time zone, as "2026-09-20". */
export function dayOf(moment: Date, timeZone: string): string {
  // en-CA writes a date as ISO does. A zone the runtime does not know falls back to UTC's day.
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(moment);
  } catch {
    return moment.toISOString().slice(0, 10);
  }
}

/** The day before. Calendar arithmetic, on the date alone: no clock change can bend it. */
export const dayBefore = (day: string): string =>
  new Date(Date.parse(`${day}T00:00:00Z`) - 86_400_000).toISOString().slice(0, 10);

export interface Streaks {
  /** Hands answered today. */
  today: number;
  /** Whether today's goal is met. */
  met: boolean;
  /** Consecutive days that met the goal, ending today, or yesterday while today is not over. */
  current: number;
  /** The longest such run in the days given. */
  best: number;
}

/**
 * Today does not break a streak until it is over: with nothing played yet this morning, four
 * days in a row are still four, and become five when today's goal is met. A day missed takes
 * it to 0. With no goal there is no streak.
 */
export function streaks(days: ReadonlyMap<string, number>, goal: number, today: string): Streaks {
  const hands = days.get(today) ?? 0;
  if (goal <= 0) return { today: hands, met: false, current: 0, best: 0 };
  const met = (day: string): boolean => (days.get(day) ?? 0) >= goal;

  let current = 0;
  for (let day = met(today) ? today : dayBefore(today); met(day); day = dayBefore(day)) current++;

  let best = 0;
  let run = 0;
  let last: string | null = null;
  for (const day of [...days.keys()].sort()) {
    if (!met(day)) continue;
    run = last !== null && dayBefore(day) === last ? run + 1 : 1;
    best = Math.max(best, run);
    last = day;
  }
  return { today: hands, met: met(today), current, best: Math.max(best, current) };
}

const dayRowSchema = z.object({
  day: z.iso.date(),
  attempts: z.number().int(),
  points: z.number(),
});

export interface DaysRead {
  /** The time zone the days were counted in: the one asked for, or UTC when the database did
      not know it. "Today" has to be told in the same one. */
  timeZone: string;
  /** Hands answered per day, for the days that have any. */
  hands: Map<string, number>;
}

const INVALID_PARAMETER = "22023";

/** The user's hands per day over the look-back, counted in their time zone. */
export async function readDays(db: SupabaseClient, timeZone: string): Promise<DaysRead> {
  const ask = (tz: string) => db.rpc("practice_days", { in_tz: tz, back_days: LOOK_BACK_DAYS });
  let asked = timeZone;
  let { data, error } = await ask(asked);
  // A zone the browser knows and Postgres does not: count in UTC rather than not at all.
  if (error?.code === INVALID_PARAMETER && asked !== "UTC") {
    asked = "UTC";
    ({ data, error } = await ask(asked));
  }
  if (error) throw new Error(`Could not read your days: ${error.message}`);
  const rows = z.array(dayRowSchema).parse(data);
  return { timeZone: asked, hands: new Map(rows.map((r) => [r.day, r.attempts])) };
}
